import {
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from '@ionic/react';
import React, { useMemo, useState } from 'react';

import { FirstAidIcon } from '@phosphor-icons/react';
import { close } from 'ionicons/icons';

import { postTrackingProblemReport } from '../../../axios/Utils/tracking';
import { iconProps } from '../../../App';
import { useUser } from '../../../context/UserContext';
import { useAlert } from '../../../hooks/UI/useAlert';
import {
  ActiveTrackingSessionSnapshot,
  TrackingProblemReport,
  TrackingProblemReportCategory,
  TrackingTechnicalSnapshot,
} from '../../../types/Tracking/types';
import {
  debugTrackingLog,
  getTrackingActiveSessionStorageKey,
  getTrackingTechnicalSnapshotStorageKey,
  parseStoredTrackingJson,
} from '../../../utils/tracking';
import { StorageService } from '../../../utils/storage';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import FormHeader from '../../UI/FormHeader';
import Navigation from '../../UI/Navigation';
import Spinner from '../../UI/Spinner';
import './ProblemHelp.scss';

type ProblemHelpProps = {
  closeModal: () => void;
  userKey?: string;
};

const ProblemHelp: React.FC<ProblemHelpProps> = ({
  closeModal,
  userKey,
}) => {
  const { user } = useUser();
  const { alertState, showAlert, hideAlert } = useAlert();
  const [category, setCategory] = useState<TrackingProblemReportCategory>('error_tecnico');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedMessage = message.trim();
  const canSubmit = !isSubmitting;

  const categoryCopy = useMemo(
    () => ({
      error_tecnico: 'Error técnico o pantalla trabada',
      problema_funcional: 'Algo no se entendió o no encontré cómo hacerlo',
      datos_incorrectos: 'Información incorrecta o incompleta',
      otro: 'Otro problema',
    }),
    [],
  );

  const buildProblemReport = async (): Promise<TrackingProblemReport | null> => {
    if (!userKey) return null;

    const [activeSessionRaw, technicalSnapshotRaw] = await Promise.all([
      StorageService.get(getTrackingActiveSessionStorageKey(userKey)),
      StorageService.get(getTrackingTechnicalSnapshotStorageKey(userKey)),
    ]);

    const activeSession =
      parseStoredTrackingJson<ActiveTrackingSessionSnapshot>(activeSessionRaw);
    const technicalSnapshot =
      parseStoredTrackingJson<TrackingTechnicalSnapshot>(technicalSnapshotRaw);

    const sessionId = activeSession?.sessionId ?? technicalSnapshot?.sessionId ?? null;
    const email = user?.email?.trim().toLowerCase() ?? null;
    const nombre = user?.nombre?.trim() ?? null;
    const apellido = user?.apellido?.trim() ?? null;
    const reporterName = [nombre, apellido].filter(Boolean).join(' ').trim() || null;

    if (!sessionId) {
      return null;
    }

    return {
      sessionId,
      revision: Math.max(
        technicalSnapshot?.revision ?? 0,
        activeSession?.diagnosticsRevision ?? 0,
      ),
      userKey,
      platform: activeSession?.platform ?? technicalSnapshot?.platform ?? 'unknown',
      appVersion: activeSession?.appVersion ?? technicalSnapshot?.appVersion ?? 'unknown',
      email,
      nombre,
      apellido,
      reporterName,
      reportedAt: new Date().toISOString(),
      source: 'profile_help_form',
      category,
      message: trimmedMessage,
      currentRoute: technicalSnapshot?.currentRoute ?? activeSession?.lastTrackedPath ?? null,
      previousRoute: technicalSnapshot?.previousRoute ?? null,
      networkStatus: technicalSnapshot?.networkStatus ?? 'unknown',
      lastSuccessfulAction: technicalSnapshot?.lastSuccessfulAction ?? null,
      lastFailedAction: technicalSnapshot?.lastFailedAction ?? null,
      recentErrors: technicalSnapshot?.recentErrors ?? [],
      recentApiFailures: technicalSnapshot?.recentApiFailures ?? [],
      recentApiSuccesses: technicalSnapshot?.recentApiSuccesses ?? [],
    };
  };

  const handleSubmit = async () => {
    if (!trimmedMessage.length) {
      showAlert({
        header: 'Contanos un poco más',
        message: 'Describí el problema con al menos un poco de detalle para que podamos seguirlo.',
        buttons: [{ text: 'Entendido' }],
      });
      return;
    }

    if (trimmedMessage.length < 10) {
      showAlert({
        header: 'Falta contexto',
        message: 'Intentá incluir qué estabas haciendo y qué viste en pantalla.',
        buttons: [{ text: 'Entendido' }],
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const report = await buildProblemReport();

      if (!report) {
        showAlert({
          header: 'No pudimos preparar el reporte',
          message: 'No encontramos una sesión técnica activa para adjuntar al problema.',
          buttons: [{ text: 'Entendido' }],
        });
        return;
      }

      const result = await postTrackingProblemReport(report);

      debugTrackingLog('Resultado del reporte manual de problema', {
        result,
        sessionId: report.sessionId,
        revision: report.revision,
        category: report.category,
      });

      if (result === 'success') {
        showAlert({
          header: 'Reporte enviado',
          message: 'Lo enviamos al equipo junto con el contexto técnico de tu sesión.',
          buttons: [{ text: 'Entendido', role: 'confirm' }],
          onDidDismiss: closeModal,
        });
        return;
      }

      if (result === 'unsupported') {
        showAlert({
          header: 'Envío todavía no disponible',
          message:
            'El formulario ya está listo en la app, pero el backend todavía no recibe estos reportes.',
          buttons: [{ text: 'Entendido', role: 'confirm' }],
        });
        return;
      }

      showAlert({
        header: 'No pudimos enviarlo',
        message: 'Intentá nuevamente en unos minutos.',
        buttons: [{ text: 'Entendido' }],
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="problem-help">
      <Navigation startIcon={close} onBack={closeModal} />

      <div className="ion-padding-horizontal">
        <FormHeader icon={<FirstAidIcon {...iconProps} />} title="Reportar un problema" />
      </div>

      <div className="ion-padding-horizontal ion-padding-bottom problem-help__content">
        <div className="problem-help__card">
          <p className="problem-help__lead">
            Si algo no funcionó como esperabas, intentá incluir estos datos cuando pidas ayuda:
          </p>

          <div className="problem-help__steps">
            <div className="problem-help__step">
              <span className="problem-help__step-index">1</span>
              <p>En qué pantalla estabas y qué acción intentabas realizar.</p>
            </div>

            <div className="problem-help__step">
              <span className="problem-help__step-index">2</span>
              <p>Qué mensaje viste en pantalla o qué comportamiento te resultó raro.</p>
            </div>

            <div className="problem-help__step">
              <span className="problem-help__step-index">3</span>
              <p>Si te pasó una sola vez o si podés repetirlo siguiendo los mismos pasos.</p>
            </div>
          </div>

        </div>

        <div className="problem-help__card">
          <IonItem>
            <IonLabel position="stacked">Tipo de problema</IonLabel>
            <IonSelect
              mode="ios"
              interface="action-sheet"
              value={category}
              placeholder="Seleccioná una opción"
              onIonChange={(event) =>
                setCategory(event.detail.value as TrackingProblemReportCategory)
              }
            >
              <IonSelectOption value="error_tecnico">
                {categoryCopy.error_tecnico}
              </IonSelectOption>
              <IonSelectOption value="problema_funcional">
                {categoryCopy.problema_funcional}
              </IonSelectOption>
              <IonSelectOption value="datos_incorrectos">
                {categoryCopy.datos_incorrectos}
              </IonSelectOption>
              <IonSelectOption value="otro">{categoryCopy.otro}</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">¿Qué pasó?</IonLabel>
            <IonTextarea
              className="problem-help__textarea"
              rows={6}
              maxlength={400}
              value={message}
              placeholder="Ejemplo: estaba en la pantalla de siniestros, toqué enviar y quedó cargando sin avanzar."
              onIonInput={(event) => {
                const nextValue = (event.target as HTMLIonTextareaElement).value;
                setMessage(typeof nextValue === 'string' ? nextValue : '');
              }}
            />
          </IonItem>

          <p className="problem-help__hint">
            Incluí qué intentabas hacer, qué viste en pantalla y si te volvió a pasar.
          </p>

          <Button
            marginBottom={false}
            noMarginTop
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {!isSubmitting ? 'Enviar reporte' : <Spinner color="light" />}
          </Button>
        </div>
      </div>

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </div>
  );
};

export default ProblemHelp;
